import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  useTheme,
  Alert,
  Snackbar,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  LinkOff as UnlinkIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import EmployeeForm from './EmployeeForm';
import EmployeeTimesheetView from './EmployeeTimesheetView';
import { makeApiCall } from '../../config/api';
import { formatScheduledDate } from '../../utils/dateUtils';
import { formatWageDisplay } from '../../utils/wageUtils';

const EmployeeManagement = () => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State for employees and filters
  const [employees, setEmployees] = useState([]);
  const [employeeStatus, setEmployeeStatus] = useState([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalEmployees, setTotalEmployees] = useState(0);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [isActive, setIsActive] = useState('true');
  
  // Dialog states
  const [openAddEmployee, setOpenAddEmployee] = useState(false);
  const [openEditEmployee, setOpenEditEmployee] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [openTimesheet, setOpenTimesheet] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  // Stats
  const [stats, setStats] = useState({});
  
  // Lookup data
  const [lookups, setLookups] = useState({
    departments: [],
    job_titles: [],
    employee_positions: []
  });

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm,
        department_id: departmentFilter,
        is_active: isActive,
        sort_by: 'employee_name',
        sort_order: 'ASC'
      });

      const response = await makeApiCall(`api/employees?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setEmployees(data.employees || []);
      setTotalEmployees(data.pagination?.total || 0);

    } catch (error) {
      console.error('Error fetching employees:', error);
      setError('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  // Fetch employee status and weekly hours
  const fetchEmployeeStatus = async () => {
    try {
      setStatusLoading(true);
      const response = await makeApiCall('api/time-tracking/admin/employee-status');

      if (response.ok) {
        const data = await response.json();
        setEmployeeStatus(data.employees || []);
      }
    } catch (error) {
      console.error('Error fetching employee status:', error);
      // Don't show error to user for status fetch, just log it
    } finally {
      setStatusLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await makeApiCall('api/employees/stats/overview');

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  // Fetch lookup data
  const fetchLookups = async () => {
    try {
      const response = await makeApiCall('api/employees/lookups/all');

      if (response.ok) {
        const data = await response.json();
        setLookups(data);
      }
    } catch (error) {
      console.error('Error fetching lookups:', error);
    }
  };

  // Handle delete employee
  const handleDeleteEmployee = async () => {
    if (!selectedEmployee) return;

    try {
      const response = await makeApiCall(`api/employees/${selectedEmployee.id}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setSuccess('Employee deleted successfully');
        fetchEmployees();
        fetchStats();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete employee');
      }
    } catch (error) {
      console.error('Error deleting employee:', error);
      setError('Failed to delete employee');
    }

    setOpenDeleteConfirm(false);
    setSelectedEmployee(null);
  };

  // Handle form success
  const handleFormSuccess = (message) => {
    setSuccess(message);
    setOpenAddEmployee(false);
    setOpenEditEmployee(false);
    setSelectedEmployee(null);
    fetchEmployees();
    fetchStats();
  };

  // Handle timesheet view
  const handleViewTimesheet = (employee) => {
    setSelectedEmployee(employee);
    setOpenTimesheet(true);
  };

  // Get status color
  const getStatusColor = (employee) => {
    if (!employee.is_active) return 'error';
    if (employee.user_id) return 'success'; // Has system access
    return 'default';
  };

  // Get status text
  const getStatusText = (employee) => {
    if (!employee.is_active) return 'Inactive';
    if (employee.user_id) return 'System Access';
    return 'Employee Only';
  };

  // Get employee status info
  const getEmployeeStatusInfo = (employee) => {
    const statusInfo = employeeStatus.find(status => status.employee_id === employee.id);
    return statusInfo || null;
  };

  // Format hours for display
  const formatHours = (hours) => {
    if (!hours || hours === 0) return '0.0h';
    return `${parseFloat(hours).toFixed(1)}h`;
  };

  // Get clock status display
  const getClockStatusDisplay = (statusInfo) => {
    if (!statusInfo || !statusInfo.user_id) {
      return {
        color: 'default',
        text: 'No Access',
        icon: '⚫'
      };
    }
    
    if (statusInfo.current_status === 'clocked_in') {
      return {
        color: 'success',
        text: 'Working',
        icon: '🟢'
      };
    }
    
    return {
      color: 'default',
      text: 'Off',
      icon: '⚪'
    };
  };

  // Use effect to fetch data
  useEffect(() => {
    fetchEmployees();
  }, [page, rowsPerPage, searchTerm, departmentFilter, isActive]);

  useEffect(() => {
    fetchStats();
    fetchLookups();
    fetchEmployeeStatus();
    
    // Set up interval to refresh employee status every 30 seconds
    const statusInterval = setInterval(fetchEmployeeStatus, 30000);
    
    return () => clearInterval(statusInterval);
  }, []);

  // Stats cards data
  const statsCards = [
    { 
      title: 'Total Employees', 
      value: stats.total_employees || 0, 
      icon: <PersonIcon />, 
      color: 'primary.main' 
    },
    { 
      title: 'Active Employees', 
      value: stats.active_employees || 0, 
      icon: <WorkIcon />, 
      color: 'success.main' 
    },
    { 
      title: 'With System Access', 
      value: stats.employees_with_users || 0, 
      icon: <BadgeIcon />, 
      color: 'info.main' 
    },
    { 
      title: 'Sales Staff', 
      value: stats.sales_persons || 0, 
      icon: <BusinessIcon />, 
      color: 'warning.main' 
    }
  ];

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }} data-demo-target="page-employees-root">
      {/* Header */}
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 'bold',
            color: '#000062',
            fontSize: { xs: '1.5rem', md: '2.125rem' },
            mb: 3
          }}
        >
          Employee Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenAddEmployee(true)}
          sx={{ 
            borderRadius: 2,
            minWidth: { xs: '100%', sm: 'auto' }
          }}
        >
          New Employee
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: { xs: 2, md: 3 } }}>
        {statsCards.map((card, index) => (
          <Grid size={{ xs: 6, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography 
                      color="textSecondary" 
                      gutterBottom 
                      variant="overline"
                      sx={{ fontSize: { xs: '0.6rem', md: '0.75rem' } }}
                    >
                      {card.title}
                    </Typography>
                    <Typography 
                      variant="h4" 
                      component="div" 
                      sx={{ 
                        color: card.color,
                        fontSize: { xs: '1.5rem', md: '2.125rem' }
                      }}
                    >
                      {card.value.toLocaleString()}
                    </Typography>
                  </Box>
                  <Avatar sx={{ 
                    bgcolor: card.color, 
                    width: { xs: 40, md: 56 }, 
                    height: { xs: 40, md: 56 }
                  }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: { xs: 2, md: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
          <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="center">
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Search Employees"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {lookups.departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.department_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={isActive}
                  label="Status"
                  onChange={(e) => setIsActive(e.target.value)}
                >
                  <MenuItem value="">All Employees</MenuItem>
                  <MenuItem value="true">Active Only</MenuItem>
                  <MenuItem value="false">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 2 }}>
              <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-start' }}>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton 
                      onClick={fetchEmployees} 
                      color="primary" 
                      size="medium"
                      disabled={loading}
                      sx={{ p: { xs: 1, md: 1.5 } }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card>
        <CardContent sx={{ p: { xs: 0, md: 2 } }}>
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Department & Position</TableCell>
                    <TableCell>Contact Info</TableCell>
                    <TableCell>Employment Details</TableCell>
                    <TableCell>Time Status</TableCell>
                    <TableCell>Weekly Hours</TableCell>
                    <TableCell>System Access</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography>Loading employees...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography>No employees found</Typography>
                      </TableCell>
                    </TableRow>
                ) : (
                  employees.map((employee) => {
                    const statusInfo = getEmployeeStatusInfo(employee);
                    const clockStatus = getClockStatusDisplay(statusInfo);
                    
                    return (
                      <TableRow key={employee.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                              {employee.employee_name?.[0] || 'E'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                                {employee.employee_name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {employee.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {employee.department_name || 'No Department'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {employee.position_name || employee.job_title || 'No Position'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            {employee.user_email && (
                              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon fontSize="small" />
                                {employee.user_email}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              Wage: {formatWageDisplay(employee.wage, employee.wage_type)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              Hired: {formatScheduledDate(employee.hire_date) || 'Not Set'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Created: {new Date(employee.created_at).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip
                              icon={<span>{clockStatus.icon}</span>}
                              label={clockStatus.text}
                              color={clockStatus.color}
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                            {statusInfo?.current_status === 'clocked_in' && statusInfo?.current_session_hours && (
                              <Typography variant="caption" display="block" color="success.main">
                                {formatHours(statusInfo.current_session_hours)} today
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'primary.main' }}>
                              {statusInfo ? formatHours(statusInfo.weekly_hours) : '0.0h'}
                            </Typography>
                            {statusInfo && statusInfo.weekly_days > 0 && (
                              <Typography variant="caption" color="text.secondary">
                                {statusInfo.weekly_days} day{statusInfo.weekly_days !== 1 ? 's' : ''}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {employee.user_id ? (
                            <Box>
                              <Chip 
                                icon={<LinkIcon />} 
                                label="Linked" 
                                color="success" 
                                size="small" 
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                @{employee.username}
                              </Typography>
                            </Box>
                          ) : (
                            <Chip 
                              icon={<UnlinkIcon />} 
                              label="No Access" 
                              color="default" 
                              size="small" 
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusText(employee)}
                            color={getStatusColor(employee)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Timesheet">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleViewTimesheet(employee)}
                                color="info"
                                disabled={!employee.user_id}
                              >
                                <ScheduleIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit Employee">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setOpenEditEmployee(true);
                              }}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Employee">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setOpenDeleteConfirm(true);
                              }}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {loading ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>Loading employees...</Typography>
              </Box>
            ) : employees.length === 0 ? (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography>No employees found</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 1 }}>
                {employees.map((employee) => {
                  const statusInfo = getEmployeeStatusInfo(employee);
                  const clockStatus = getClockStatusDisplay(statusInfo);
                  
                  return (
                    <Card 
                      key={employee.id} 
                      sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 3
                        }
                      }}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box display="flex" alignItems="center" flex={1}>
                            <Avatar sx={{ mr: 1.5, bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                              {employee.employee_name?.[0] || 'E'}
                            </Avatar>
                            <Box flex={1}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                                {employee.employee_name}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.75rem' }}>
                                ID: {employee.id} • {clockStatus.icon} {clockStatus.text}
                              </Typography>
                            </Box>
                          </Box>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={getStatusText(employee)}
                              color={getStatusColor(employee)}
                              size="small"
                              sx={{ fontSize: '0.7rem', height: '20px' }}
                            />
                            <Box>
                              <Tooltip title="View Timesheet">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleViewTimesheet(employee)}
                                    color="info"
                                    disabled={!employee.user_id}
                                  >
                                    <ScheduleIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedEmployee(employee);
                                    setOpenEditEmployee(true);
                                  }}
                                  color="primary"
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedEmployee(employee);
                                    setOpenDeleteConfirm(true);
                                  }}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 1 }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 'medium', fontSize: '0.75rem', mb: 0.5 }}>
                              {employee.department_name || 'No Department'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                              {employee.position_name || employee.job_title || 'No Position'}
                            </Typography>
                            {employee.user_email && (
                              <Box display="flex" alignItems="center" mt={0.5}>
                                <EmailIcon sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                                <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                  {employee.user_email?.length > 15 ? `${employee.user_email.substring(0, 15)}...` : employee.user_email}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                              Week: {statusInfo ? formatHours(statusInfo.weekly_hours) : '0.0h'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                              Wage: {formatWageDisplay(employee.wage, employee.wage_type)}
                            </Typography>
                            {employee.user_id ? (
                              <Box>
                                <Chip 
                                  icon={<LinkIcon />} 
                                  label="System Access" 
                                  color="success" 
                                  size="small" 
                                  sx={{ fontSize: '0.6rem', height: '18px', mb: 0.5 }}
                                />
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                  @{employee.username}
                                </Typography>
                              </Box>
                            ) : (
                              <Chip 
                                icon={<UnlinkIcon />} 
                                label="No Access" 
                                color="default" 
                                size="small" 
                                sx={{ fontSize: '0.6rem', height: '18px' }}
                              />
                            )}
                          </Box>
                        </Box>
                        
                        {statusInfo?.current_status === 'clocked_in' && statusInfo?.current_session_hours && (
                          <Box sx={{ mb: 1, p: 1, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                            <Typography variant="caption" color="success.main" sx={{ fontSize: '0.7rem', fontWeight: 'medium' }}>
                              🟢 Currently working • {formatHours(statusInfo.current_session_hours)} today
                            </Typography>
                          </Box>
                        )}
                        
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                          Hired: {formatScheduledDate(employee.hire_date) || 'Not Set'} • 
                          Created: {new Date(employee.created_at).toLocaleDateString()}
                        </Typography>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalEmployees}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            sx={{ mt: { xs: 1, md: 0 } }}
          />
        </CardContent>
      </Card>

      {/* Add Employee Dialog */}
      <Dialog 
        open={openAddEmployee} 
        onClose={() => setOpenAddEmployee(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Add New Employee</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          <EmployeeForm
            onSuccess={handleFormSuccess}
            onCancel={() => setOpenAddEmployee(false)}
            lookups={lookups}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog 
        open={openEditEmployee} 
        onClose={() => setOpenEditEmployee(false)} 
        maxWidth="md" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            m: { xs: 1, sm: 2 },
            height: { xs: 'calc(100% - 16px)', sm: 'auto' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: '90vh' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}>Edit Employee</DialogTitle>
        <DialogContent sx={{ p: { xs: 1, md: 2 } }}>
          {selectedEmployee && (
            <EmployeeForm
              key={selectedEmployee.id}
              employee={selectedEmployee}
              onSuccess={handleFormSuccess}
              onCancel={() => setOpenEditEmployee(false)}
              lookups={lookups}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteConfirm} onClose={() => setOpenDeleteConfirm(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete employee "{selectedEmployee?.employee_name}"? This action cannot be undone.
            {selectedEmployee?.user_id && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This employee has system access. Consider unlinking the user account first.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteConfirm(false)}>Cancel</Button>
          <Button onClick={handleDeleteEmployee} color="error" variant="contained">
            Delete Employee
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Timesheet Dialog */}
      <EmployeeTimesheetView
        employee={selectedEmployee}
        open={openTimesheet}
        onClose={() => {
          setOpenTimesheet(false);
          setSelectedEmployee(null);
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmployeeManagement; 